import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import { Notification } from "@rio-cloud/rio-uikit";

import { ConfigPaginator } from "../../../components/PaginatorSize";

import { deleteAreById, getAllAreas } from "../../../services/areas";
import { defineAreasRework, defineAvoidAreas } from "../../../store/routes/RoutesSlice";
import { CreateAreaReworkPayload } from "../../../types/areas";
import { filteredAreas } from "./filteredAreas";
import { mountDashboardAreas } from "./mountDashboardAreas";

const PAGE_SIZE = 10;
export const useAreasControl = () => {
    const dispatch = useDispatch();

    const [openModal, setOpenModal] = useState(false);
    const [fieldSort, setFieldSort] = useState("createdAt");
    const [sort, setSort] = useState<{ [name: string]: string }>({ "createdAt": "desc" });
    const [page, setPage] = useState<number>(1);
    const [pages, setPages] = useState<number>(1);
    const [totalSize, setTotalSize] = useState<number>(0);
    const [show, setShow] = useState(false);
    const [selectedAreaId, setSelectedAreaId] = useState<string | undefined>();
    const [showCreate, setShowCreate] = useState(false);
    const [areas, setAreas] = useState<CreateAreaReworkPayload[]>([]);
    const [searchValue, setSearchValue] = useState("");
    const [isLoading, setIsLoading] = useState(true);

    const [dashboard, setDashboard] = useState({
        globalInterests: 0,
        opcionalInterests: 0,
        globalAvoids: 0,
        optinalAvoids: 0,
    });

    useEffect(() => {
        loadAreaAndClean();
    }, []);

    useEffect(() => {
        loadMoreAreas();
    }, [searchValue, sort, fieldSort]);

    const loadAreaAndClean = async () => {
        const { areas, pages, totalItems, summary } = await searchAreas(1, searchValue);
        setAreas(areas);
        setPages(pages);
        setTotalSize(totalItems);
        setDashboard(mountDashboardAreas(summary));
    }

    const searchAreas = async (page: number, searchValue: string) => {
        try {
            const { items, pages, totalItems, summary } = await getAllAreas(searchValue, 1, page * PAGE_SIZE, `${fieldSort},${sort[fieldSort]}`);
            setIsLoading(false)
            return { areas: items, pages: pages, totalItems: totalItems, summary: summary };
        } catch (error) {
            Notification.error("Não foi possível buscar as áreas.");
            setIsLoading(false)
            return { areas: [], pages: 0, totalItems: 0, summary: [] };
        }
    }

    const loadMoreAreas = async (newPage?: number) => {
        const res = await searchAreas(newPage || page, searchValue);
        setAreas(res.areas);
        setTotalSize(res.totalItems);
    }

    const handlePage = (config: ConfigPaginator) => {
        const nextPage = Math.round(config.next / PAGE_SIZE);
        setPage(nextPage);
        loadMoreAreas(nextPage);
    };

    const handleResetPage = () => {
        setPage(1);
        loadMoreAreas(1);
    };

    const handleOpenModal = () => {
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
    };

    const handleOpenArea = async (id: string) => {
        setSelectedAreaId(id);
        setShowCreate(true);
    };

    const handleDeleteArea = async (id?: string) => {
        try {
            if (!id) return;
            await deleteAreById(id);
            Notification.info("Área excluída com sucesso!");
            await loadAreaAndClean();
            setShow(false);
        } catch (error) {
            Notification.error("Não foi possível buscar as áreas.");
        }
    };

    const handleCreateNewArea = () => {
        dispatch(defineAvoidAreas({ formatted: undefined }));
        dispatch(defineAreasRework([]));
        setSelectedAreaId(undefined);
        setShowCreate(true);
    };

    const handleSort = (dir: string, field: string) => {
        setFieldSort(field);
        setSort({ [field]: dir });
    };

    const handleSearchValueChange = async (text: string) => {
        setSearchValue(text);
        const searchValueInTrips = areas.filter(f => filteredAreas(text, f)).length > 0;
        if (text.length === 0) {
            const { areas, pages, totalItems, summary } = await searchAreas(1, "");
            setAreas(areas);
            setPages(pages);
            setTotalSize(totalItems);
            setDashboard(mountDashboardAreas(summary));
        } else if (!searchValueInTrips) {
            const { areas, pages, totalItems } = await searchAreas(text.length >= 0 ? -1 : page, text);
            setAreas(areas);
            setPages(pages);
            setTotalSize(totalItems);
        }
        setPage(1);
    }

    const handleCancelCreate = (shouldRefresh?: boolean) => {
        setShowCreate(false);
        setSelectedAreaId(undefined);
        if (shouldRefresh) loadAreaAndClean();
    };

    return {
        // Estados
        openModal,
        fieldSort,
        sort,
        page,
        pages,
        totalSize,
        show,
        selectedAreaId,
        showCreate,
        areas,
        searchValue,
        dashboard,
        PAGE_SIZE,

        // Funções
        handleOpenModal,
        handleCloseModal,
        handleOpenArea,
        handleDeleteArea,
        handleCreateNewArea,
        handleSort,
        handleSearchValueChange,
        handlePage,
        handleResetPage,
        handleCancelCreate,
        setShow,
        isLoading,
    };
}; 